import { createSelector } from 'reselect'
import * as dateFns from 'date-fns'

// Constants
import { FINANCIAL_FIELDS } from 'Constants'

// Utils
const DOCUMENT_DISPLAY_MAP = { folio: 'Hotel Folio', invoice: 'Invoice', receipt: 'Receipt' }

const getComplianceStats = ({ documentStats }) => documentStats.complianceStats
const getDocumentTypeStats = ({ documentStats }) => documentStats.documentTypeStats
const getFetchingComplianceStats = ({ documentStats }) => documentStats.fetchingComplianceStats
const getFetchingDocumentTypeStats = ({ documentStats }) => documentStats.fetchingDocumentTypeStats
const getFetchingPaymentStats = ({ documentStats }) => documentStats.fetchingPaymentStats
const getFetchingTotalUploadsStats = ({ documentStats }) => documentStats.fetchingTotalUploadsStats
const getPaymentStats = ({ documentStats }) => documentStats.paymentStats
const getTotalUploadsStats = ({ documentStats }) => documentStats.totalUploadsStats
const getFetchingNewInvoicesStats = ({ documentStats }) => documentStats.fetchingNewInvoicesStats
const getNewInvoicesStats = ({ documentStats }) => documentStats.newInvoicesStats
const getFetchingPendingInvoicesStats = ({ documentStats }) =>
  documentStats.fetchingPendingInvoicesStats
const getPendingInvoicesStats = ({ documentStats }) => documentStats.pendingInvoicesStats
const getFetchingDiscountInvoicesStats = ({ documentStats }) =>
  documentStats.fetchingDiscountInvoicesStats
const getDiscountInvoicesStats = ({ documentStats }) => documentStats.discountInvoicesStats
const getFetchingPastDueInvoicesStats = ({ documentStats }) =>
  documentStats.fetchingPastDueInvoicesStats
const getPastDueInvoicesStats = ({ documentStats }) => documentStats.pastDueInvoicesStats
const getFetchingExceptionInvoicesStats = ({ documentStats }) =>
  documentStats.fetchingExceptionInvoicesStats
const getExceptionInvoicesStats = ({ documentStats }) => documentStats.exceptionInvoicesStats
const getFetchingPOInvoicesStats = ({ documentStats }) => documentStats.fetchingPOInvoicesStats
const getPOInvoicesStats = ({ documentStats }) => documentStats.poInvoicesStats
const getFetchingVendorsInvoicesStats = ({ documentStats }) =>
  documentStats.fetchingVendorsInvoicesStats
const getVendorsInvoicesStats = ({ documentStats }) => documentStats.vendorsInvoicesStats
const getFetchingPaymentInvoicesStats = ({ documentStats }) =>
  documentStats.fetchingPaymentInvoicesStats
const getPaymentInvoicesStats = ({ documentStats }) => ({
  paymentInvoicesStats: documentStats.paymentInvoicesStats,
  next: documentStats?.next,
  previous: documentStats?.previous,
})
const getPaymentStatuses = ({ ledgerFilter }) => ledgerFilter?.filters?.paymentStatuses
const getCurrentDate = () => dateFns.startOfDay(new Date()).toISOString()

const isFinancialField = ({ name }) => {
  return FINANCIAL_FIELDS.includes(name)
}
const getAttribute = (name, attributes = []) => {
  return attributes.find(({ type }) => {
    return type.name === name
  })
}

const getFinancialValues = (attributes = []) => {
  return attributes.reduce((acc, { type, value }) => {
    return isFinancialField(type) ? { ...acc, [type.name.toLowerCase()]: value } : acc
  }, {})
}

export const getLedgerValueFromMapping = (name, tagType, mappings = []) => {
  const mapping = mappings.find((item) => {
    return item[tagType]?.type.name === name
  })

  if (mapping) {
    return mapping[tagType]?.value
  }
}

export const documentStatsSelectors = {
  getComplianceStats: createSelector(getComplianceStats, (complianceStats) => {
    if (!complianceStats.length) {
      return []
    }
    const high = complianceStats.find(({ riskScore }) => riskScore === 'high')
    const low = complianceStats.find(({ riskScore }) => riskScore === 'low')
    return [low?.totalDocuments || 0, high?.totalDocuments || 0]
  }),
  getFetchingComplianceStats: createSelector(
    getFetchingComplianceStats,
    (fetchingComplianceStats) => fetchingComplianceStats,
  ),
  getDocumentTypeStats: createSelector(getDocumentTypeStats, (documentTypeStats) => {
    if (!documentTypeStats.length) {
      return []
    }
    const receipt = documentTypeStats.find(({ docType }) => docType === 'receipt')
    const invoice = documentTypeStats.find(({ docType }) => docType === 'invoice')
    const folio = documentTypeStats.find(({ docType }) => docType === 'folio')
    return [receipt?.totalDocuments || 0, invoice?.totalDocuments || 0, folio?.totalDocuments || 0]
  }),
  getFetchingDocumentTypeStats: createSelector(
    getFetchingDocumentTypeStats,
    (fetchingDocumentTypeStats) => fetchingDocumentTypeStats,
  ),
  getFetchingPaymentStats: createSelector(
    getFetchingPaymentStats,
    (fetchingPaymentStats) => fetchingPaymentStats,
  ),
  getFetchingTotalUploadsStats: createSelector(
    getFetchingTotalUploadsStats,
    (fetchingTotalUploadsStats) => fetchingTotalUploadsStats,
  ),
  getPaymentStats: createSelector(getPaymentStats, ({ oneMonth, oneWeek, pastDue }) => {
    if (!Number.isInteger(oneMonth) || !Number.isInteger(oneWeek) || !Number.isInteger(pastDue)) {
      return []
    }
    return [oneMonth, oneWeek, pastDue]
  }),
  getFetchingNewInvoicesStats: createSelector(
    getFetchingNewInvoicesStats,
    (fetchingNewInvoicesStats) => fetchingNewInvoicesStats,
  ),
  getNewInvoicesStats: createSelector(getNewInvoicesStats, (newInvoicesStats) => newInvoicesStats),
  getFetchingPendingInvoicesStats: createSelector(
    getFetchingPendingInvoicesStats,
    (fetchingPendingInvoicesStats) => fetchingPendingInvoicesStats,
  ),
  getPendingInvoicesStats: createSelector(
    getPendingInvoicesStats,
    (pendingInvoicesStats) => pendingInvoicesStats,
  ),
  getFetchingDiscountInvoicesStats: createSelector(
    getFetchingDiscountInvoicesStats,
    (fetchingDiscountInvoicesStats) => fetchingDiscountInvoicesStats,
  ),
  getDiscountInvoicesStats: createSelector(
    getDiscountInvoicesStats,
    (discountInvoicesStats) => discountInvoicesStats,
  ),
  getFetchingPastDueInvoicesStats: createSelector(
    getFetchingPastDueInvoicesStats,
    (fetchingPastDueInvoicesStats) => fetchingPastDueInvoicesStats,
  ),
  getPastDueInvoicesStats: createSelector(
    getPastDueInvoicesStats,
    (pastDueInvoicesStats) => pastDueInvoicesStats,
  ),

  getFetchingExceptionInvoicesStats: createSelector(
    getFetchingExceptionInvoicesStats,
    (fetchingExceptionInvoicesStats) => fetchingExceptionInvoicesStats,
  ),
  getExceptionInvoicesStats: createSelector(
    getExceptionInvoicesStats,
    (exceptionInvoicesStats) => exceptionInvoicesStats,
  ),
  getFetchingPOInvoicesStats: createSelector(
    getFetchingPOInvoicesStats,
    (fetchingPOInvoicesStats) => fetchingPOInvoicesStats,
  ),
  getPOInvoicesStats: createSelector(getPOInvoicesStats, (poInvoicesStats) => poInvoicesStats),
  getFetchingVendorsInvoicesStats: createSelector(
    getFetchingVendorsInvoicesStats,
    (fetchingVendorsInvoicesStats) => fetchingVendorsInvoicesStats,
  ),
  getVendorsInvoicesStats: createSelector(
    getVendorsInvoicesStats,
    (vendorsInvoicesStats) => vendorsInvoicesStats,
  ),
  getFetchingPaymentInvoicesStats: createSelector(
    getFetchingPaymentInvoicesStats,
    (fetchingPaymentInvoicesStats) => fetchingPaymentInvoicesStats,
  ),
  getPaymentInvoicesStats: createSelector(
    [getPaymentInvoicesStats],
    ({ next, previous, paymentInvoicesStats }) => {
      return {
        paymentInvoicesStats: paymentInvoicesStats?.map(
          ({ documentType, merchant, orders, tagMappings, ...paymentInvoicesStat }, index) => {
            return {
              ...paymentInvoicesStat,
              documentType: DOCUMENT_DISPLAY_MAP[documentType.name],
              currency: orders[0]?.currency?.symbol,
              dueDate: getAttribute('DueDate', orders[0]?.attributes)?.value,
              documentNumber: getAttribute('DocNumber', orders[0]?.attributes)?.value,
              purchaseOrderNumber: getAttribute('PurchaseOrderNumber', orders[0]?.attributes)
                ?.value,
              expenseCategory:
                orders[0]?.expenseCategory?.name ||
                getLedgerValueFromMapping('ExpenseCategory', 'toTag', tagMappings)?.name ||
                merchant?.category?.expenseCategory?.name,
              glCode:
                orders[0]?.glCode?.value ||
                getLedgerValueFromMapping('GLcode', 'toTag', tagMappings),
              ledgerIndex: index,
              merchant: merchant?.name,
              merchantImage: merchant?.image || merchant?.category?.image,
              paymentStatus: orders[0]?.paymentStatus?.value.toLowerCase(),
              workflowStatus: orders[0]?.workflowStatus?.value.toLowerCase(),
              ...getFinancialValues(orders[0]?.attributes),
            }
          },
        ),
        next,
        previous,
      }
    },
  ),
  getPaymentStatuses: createSelector(getPaymentStatuses, (paymentStatuses) => paymentStatuses),
  getCurrentDate: createSelector(getCurrentDate, (currentDate) => currentDate),
  getTotalUploadsStats: (dates) =>
    createSelector(getTotalUploadsStats, (totalUploadsStats) => {
      if (!totalUploadsStats.length) {
        return []
      }
      return dates.map((intervalDate) => {
        const dataForDate = totalUploadsStats.find(({ date, totalDocuments }) => {
          return dateFns.isSameDay(intervalDate, dateFns.toDate(Date.parse(date)))
        })
        return dataForDate?.totalDocuments ?? 0
      })
    }),
}
